import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { defer, Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from "./confirm-dialog.component";

export interface ConfirmOptions {
  okLabel?: string;
  cancelLabel?: string;
}

export interface AlertOptions {
  okLabel?: string;
}

let nextMessageId = 0;

/**
 * In-page replacement for the native `confirm()` / `alert()` dialogs, which
 * render as "An embedded page at … says" when Studio runs inside the LiveE
 * iframe, cannot be styled or translated and block browser automation.
 *
 * The returned Observables are cold: the dialog opens on subscribe, emits
 * exactly one value and completes.
 */
@Injectable()
export class ConfirmService {
  constructor(private readonly _dialog: MatDialog) {}

  /** Emits `true` for OK; `false` for Cancel, Escape or backdrop. */
  confirm(message: string, opts: ConfirmOptions = {}): Observable<boolean> {
    return this._open({
      kind: "confirm",
      message,
      okLabel: opts.okLabel,
      cancelLabel: opts.cancelLabel
    }).pipe(map((result) => result === true));
  }

  /**
   * Single-button notice. Emits once when the dialog is closed (OK, Escape or
   * backdrop).
   */
  alert(message: string, opts: AlertOptions = {}): Observable<void> {
    return this._open({
      kind: "alert",
      message,
      okLabel: opts.okLabel
    }).pipe(map(() => undefined));
  }

  private _open(
    data: Omit<ConfirmDialogData, "messageId">
  ): Observable<boolean | undefined> {
    return defer(() => {
      const dialogData: ConfirmDialogData = {
        ...data,
        messageId: `youm-confirm-message-${nextMessageId++}`
      };

      // MatDialogConfig (v14) does not type `ariaModal`, but the config is
      // handed through to the CDK dialog container, which renders it.
      const config: MatDialogConfig<ConfirmDialogData> & {
        ariaModal: boolean;
      } = {
        data: dialogData,
        role: data.kind === "alert" ? "alertdialog" : "dialog",
        ariaModal: true,
        ariaLabelledBy: dialogData.messageId,
        autoFocus:
          data.kind === "alert"
            ? '[data-testid="alert-ok"]'
            : '[data-testid="confirm-ok"]',
        restoreFocus: true,
        panelClass: "youm-confirm-dialog"
      };

      return this._dialog
        .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(
          ConfirmDialogComponent,
          config
        )
        .afterClosed();
    }).pipe(take(1));
  }
}
